import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import ParentEditModal from "./ParentEditModal";
import ParentLinkStudentModal from "./ParentLinkStudentModal";
import ParentViewModal from "./ParentViewModal";
import BulkParentAccountsModal from "./BulkParentAccountsModal";

function Parents() {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewParent, setViewParent] = useState(null);
  const [editParent, setEditParent] = useState(null);
  const [linkParent, setLinkParent] = useState(null);
  const [resetDetails, setResetDetails] = useState(null);
  const [showBulkParents, setShowBulkParents] = useState(false);

  const filteredParents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return parents;
    return parents.filter((parent) => {
      return (
        parent.full_name?.toLowerCase().includes(term) ||
        parent.email?.toLowerCase().includes(term) ||
        parent.phone?.toLowerCase().includes(term) ||
        parent.occupation?.toLowerCase().includes(term)
      );
    });
  }, [parents, searchTerm]);

  const activeCount = parents.filter((parent) => parent.is_active).length;
  const pendingCount = parents.filter((parent) => parent.status === "PENDING").length;
  const linkedCount = parents.reduce((total, parent) => total + (parent.linked_students_count || 0), 0);

  const fetchParents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("parents/");
      setParents(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load parents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParents();
  }, []);

  const updateParentInState = (parent) => {
    setParents((prev) => prev.map((item) => (item.id === parent.id ? parent : item)));
    setEditParent(null);
    setViewParent(null);
    setLinkParent(parent);
  };

  const handleParentUpdated = (parent) => {
    setParents((prev) => prev.map((item) => (item.id === parent.id ? parent : item)));
    setEditParent(null);
    setViewParent(null);
    setLinkParent(null);
  };

  const approveParent = async (parent) => {
    try {
      const response = await axiosInstance.post(`parents/${parent.id}/approve/`);
      handleParentUpdated(response.data.parent);
      toast.success("Parent approved successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to approve parent.");
    }
  };

  const resetParentPassword = async (parent) => {
    if (!window.confirm(`Reset password for ${parent.full_name}?`)) return;

    try {
      const response = await axiosInstance.post(`parents/${parent.id}/reset-password/`);
      const data = response.data;
      handleParentUpdated(data.parent);
      setResetDetails({
        full_name: data.parent.full_name,
        email: data.email,
        username: data.username,
        temporary_password: data.temporary_password,
      });
      toast.success("Parent password reset successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reset parent password.");
    }
  };

  const copyResetDetails = async () => {
    if (!resetDetails) return;
    const text = `Parent: ${resetDetails.full_name}
Email: ${resetDetails.email}
Username: ${resetDetails.username}
Temporary Password: ${resetDetails.temporary_password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("New password details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>Parents</h3>
          <p className="text-muted mb-0">Review parent accounts, approve access, and link parents to students.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-success" onClick={() => setShowBulkParents(true)}>
            <i className="bi bi-people-fill me-2"></i>
            Bulk Parent Accounts
          </button>
          <button className="btn btn-light border" onClick={fetchParents} title="Refresh parents">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      {resetDetails && (
        <div className="alert alert-warning border-0 shadow-sm mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h6 className="fw-bold mb-1">Parent password reset</h6>
              <div><strong>{resetDetails.full_name}</strong> can login using email or username.</div>
              <small className="text-muted">New temporary password: {resetDetails.temporary_password}</small>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-warning btn-sm" onClick={copyResetDetails}><i className="bi bi-clipboard-check me-2"></i>Copy Details</button>
              <button className="btn btn-light btn-sm" onClick={() => setResetDetails(null)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Total Parents</small><h3 className="fw-bold mb-0">{parents.length}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-people fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Active</small><h3 className="fw-bold mb-0">{activeCount}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-check2-circle fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Pending</small><h3 className="fw-bold mb-0">{pendingCount}</h3></div><div className="rounded-3 p-3 text-warning" style={{ background: "#fff8e6" }}><i className="bi bi-hourglass-split fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Linked Students</small><h3 className="fw-bold mb-0">{linkedCount}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-link-45deg fs-3"></i></div></div></div></div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Parent Accounts</h5>
            <input type="search" className="form-control" style={{ maxWidth: 320 }} placeholder="Search parent..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2"></span>Loading parents...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Parent</th>
                    <th>Phone</th>
                    <th>Occupation</th>
                    <th>Linked Students</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParents.map((parent) => (
                    <tr key={parent.id}>
                      <td><div className="fw-bold">{parent.full_name}</div><small className="text-muted">{parent.email}</small></td>
                      <td>{parent.phone || "Not set"}</td>
                      <td><div>{parent.occupation || "Not set"}</div><small className="text-muted">{parent.employer || ""}</small></td>
                      <td><span className="badge text-bg-light border">{parent.linked_students_count || 0}</span></td>
                      <td><span className={`badge ${parent.is_active ? "text-bg-success" : "text-bg-secondary"}`}>{parent.is_active ? parent.status || "ACTIVE" : "INACTIVE"}</span></td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-light border me-2" title="View parent" onClick={() => setViewParent(parent)}><i className="bi bi-eye text-success"></i></button>
                        <button className="btn btn-sm btn-light border me-2" title="Edit parent" onClick={() => setEditParent(parent)}><i className="bi bi-pencil-square text-success"></i></button>
                        <button className="btn btn-sm btn-light border me-2" title="Link student" onClick={() => setLinkParent(parent)}><i className="bi bi-link-45deg text-success"></i></button>
                        <button className="btn btn-sm btn-light border me-2" title="Reset password" onClick={() => resetParentPassword(parent)}><i className="bi bi-key text-warning"></i></button>
                        {parent.status === "PENDING" && (
                          <button className="btn btn-sm btn-light border" title="Approve parent" onClick={() => approveParent(parent)}><i className="bi bi-check2-circle text-success"></i></button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredParents.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-muted py-4">No parents found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ParentViewModal parent={viewParent} onClose={() => setViewParent(null)} onEdit={(parent) => { setViewParent(null); setEditParent(parent); }} onLinkStudent={(parent) => { setViewParent(null); setLinkParent(parent); }} />
      <ParentEditModal parent={editParent} onClose={() => setEditParent(null)} onUpdated={handleParentUpdated} />
      <ParentLinkStudentModal parent={linkParent} onClose={() => setLinkParent(null)} onUpdated={updateParentInState} />
      {showBulkParents && (
        <BulkParentAccountsModal
          onClose={() => setShowBulkParents(false)}
          onCompleted={fetchParents}
        />
      )}
    </div>
  );
}

export default Parents;
